import { AxiosResponse } from 'axios';
import { apiToken } from '../config/ApiService';

export type ExportFormat = 'csv' | 'xlsx';

export type UserStatusFilter = 'active' | 'inactive' | 'all';

export interface UsersExportParams {
  format: ExportFormat;
  status?: UserStatusFilter;
  group_ids?: number[];
}

export type LockedDealsSection = 'net' | 'resources' | 'both';

export interface LockedDealsExportParams {
  format: ExportFormat;
  sections: LockedDealsSection;
  locked_from?: string; // YYYY-MM-DD
  locked_to?: string;   // YYYY-MM-DD
  region_ids?: number[];
}

export interface PricingDetailsExportParams {
  format: ExportFormat;
  version_id?: number; // defaults to the latest base price version
}

function extractFilename(response: AxiosResponse<Blob>, fallback: string): string {
  const header =
    response.headers['content-disposition'] || response.headers['Content-Disposition'];
  if (!header) return fallback;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  return match ? decodeURIComponent(match[1]) : fallback;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
}

function extractErrorMessage(error: any, fallback: string): string {
  return error?.response?.data?.detail || error?.message || fallback;
}

async function downloadExport(
  path: string,
  params: Record<string, any>,
  fallbackBaseName: string,
  format: ExportFormat,
): Promise<void> {
  try {
    const response = await apiToken.get<Blob>(path, {
      params,
      responseType: 'blob',
      paramsSerializer: {
        indexes: null, // group_ids=1&group_ids=2 (FastAPI list style)
      },
    });
    const filename = extractFilename(
      response,
      `${fallbackBaseName}.${format}`,
    );
    triggerBlobDownload(response.data, filename);
  } catch (error: any) {
    // Error body is a Blob when responseType is blob — parse it back to text
    if (error?.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const parsed = JSON.parse(text);
        throw new Error(parsed.detail || 'Failed to download export');
      } catch (parseError: any) {
        if (parseError instanceof Error && parseError.message !== 'Failed to download export') {
          throw parseError;
        }
        throw new Error('Failed to download export');
      }
    }
    throw new Error(extractErrorMessage(error, 'Failed to download export'));
  }
}

export const exportService = {
  exportUsers: (params: UsersExportParams) =>
    downloadExport('/admin/exports/users', params, 'users', params.format),

  exportLockedDeals: (params: LockedDealsExportParams) => {
    const fallbackBase =
      params.sections === 'net'
        ? 'locked_deals_net_prices'
        : params.sections === 'resources'
        ? 'locked_deals_resource_prices'
        : 'locked_deals';
    // Both + CSV → server returns a .zip
    const fallbackExt =
      params.sections === 'both' && params.format === 'csv' ? 'zip' : params.format;
    return downloadExport(
      '/admin/exports/locked-deals',
      params,
      fallbackBase,
      fallbackExt as ExportFormat,
    );
  },

  exportPricingDetails: (params: PricingDetailsExportParams) =>
    downloadExport(
      '/admin/exports/pricing-details',
      params,
      'pricing_details',
      params.format,
    ),
};
